import { UserPermissions } from "../types/rbac";

// Define all available modules
export const AVAILABLE_MODULES = [
  'users',
  'customers', 
  'orders',
  'branches',
  'sites',
  'workflows',
  'dashboard',
  'realtime_dashboard',
  'reports',
  'settings',
  'stages', // Superadmin-only by default (can be granted via RBAC)
  'items', // Superadmin-only by default (can be granted via RBAC)
  'item_subtype_definitions', // Superadmin-only by default (can be granted via RBAC)
  'rbac',
  'count_master',
  'admin_stage_status',
  'workflow',
  'workflow management',
  'workflow assignments',
  'dispatch', // Dispatch module for supervisor dashboard
  'contacts', // Contacts module
  'leads', // Leads module
  // Masters parent and children
  'master',
  'states',
  'cities',
  'colors'
] as const;

// Define all available actions
export const AVAILABLE_ACTIONS = [
  'create',
  'read', 
  'update',
  'delete',
  'manage_roles'
] as const;

/**
 * Apply global/default overrides to a raw permissions object coming
 * from the backend. This is the single place where we enforce
 * "always-on" modules for every user, regardless of role.
 *
 * Current rule:
 * - All users get full CRUD access to:
 *   - leads
 *   - contacts
 */
export const applyGlobalPermissionOverrides = (
  userPermissions: UserPermissions | null | undefined
): UserPermissions => {
  const normalized: UserPermissions = userPermissions ? { ...userPermissions } : {};

  const ensureFullCrudForModule = (module: string) => {
    const existingModulePerms = normalized[module] ? { ...normalized[module] } : {};
    const crudActions: Array<"create" | "read" | "update" | "delete"> = [
      "create",
      "read",
      "update",
      "delete",
    ];

    crudActions.forEach((action) => {
      existingModulePerms[action] = true;
    });

    normalized[module] = existingModulePerms;
  };

  // Always-on business modules for every authenticated user
  ensureFullCrudForModule("leads");
  ensureFullCrudForModule("contacts");

  return normalized;
};

/**
 * Check if user has a specific permission
 * @param userPermissions - User's permissions object
 * @param module - Module name (e.g., 'users', 'orders')
 * @param action - Action name (e.g., 'create', 'read', 'update', 'delete')
 * @returns boolean - True if user has the permission
 */
export const hasPermission = (
  userPermissions: UserPermissions | null | undefined,
  module: string,
  action: string
): boolean => {
  // Strict check: if no permissions object, deny access
  if (!userPermissions || typeof userPermissions !== 'object') return false;
  
  // Enforce Master parent dependency for inner master modules
  const masterChildren = new Set(['states', 'cities', 'colors', 'stages', 'items', 'item_subtype_definitions', 'count_master', 'sets', 'settings']);
  const requiresMaster = masterChildren.has(module);

  // Strict check: permission must be explicitly set to true
  // Check if module exists in permissions
  const modulePermissions = userPermissions[module];
  if (!modulePermissions || typeof modulePermissions !== 'object') return false;
  
  // Check if action exists and is explicitly true
  const hasModulePermission = modulePermissions[action] === true;
  if (!hasModulePermission) return false;

  // If this is a master child module, also require master permission
  if (requiresMaster) {
    // For reads, require master.read; for writes, require corresponding action on master
    const masterAction = action === 'read' ? 'read' : action;
    const masterPermissions = userPermissions['master'];
    if (!masterPermissions || typeof masterPermissions !== 'object') return false;
    return masterPermissions[masterAction] === true;
  }

  return true;
};

/**
 * Check if user has any permission for a module
 * @param userPermissions - User's permissions object
 * @param module - Module name
 * @returns boolean - True if user has any permission for the module
 */
export const hasModulePermission = (
  userPermissions: UserPermissions | null | undefined,
  module: string
): boolean => {
  if (!userPermissions) return false;

  // Check if user has any permission for the module
  const modulePermissions = userPermissions[module];
  if (!modulePermissions) return false;

  return Object.values(modulePermissions).some(
    (permission) => permission === true
  );
};

/**
 * Get all permissions for a specific module
 * @param userPermissions - User's permissions object
 * @param module - Module name
 * @returns Record<string, boolean> - Object with action permissions
 */
export const getModulePermissions = (
  userPermissions: UserPermissions | null | undefined,
  module: string
): Record<string, boolean> => {
  if (!userPermissions) return {};

  return userPermissions[module] || {};
};

/**
 * Check if user has role-based access
 * @param userRole - User's role
 * @param requiredRoles - Array of required roles
 * @returns boolean - True if user has one of the required roles
 */
export const hasRole = (userRole: string, requiredRoles: string[]): boolean => {
  if (!userRole || !requiredRoles.length) return false;

  // Super admin role has access to everything
  if (userRole === "super_admin") return true;

  // Admin role has access to everything
  if (userRole === "admin") return true;

  return requiredRoles.includes(userRole);
};

/**
 * Check if user can manage roles (super admin or admin)
 * @param userRole - User's role
 * @returns boolean - True if user can manage roles
 */
export const canManageRoles = (userRole: string): boolean => {
  return hasRole(userRole, ["super_admin", "admin"]);
};

/**
 * Check if user can manage users
 * @param userRole - User's role
 * @returns boolean - True if user can manage users
 */
export const canManageUsers = (userRole: string): boolean => {
  return hasRole(userRole, ["super_admin", "admin", "branch_admin"]);
};

/**
 * Check if user can access branch-specific data
 * @param userRole - User's role
 * @returns boolean - True if user has branch access
 */
export const hasBranchAccess = (userRole: string): boolean => {
  return hasRole(userRole, ["super_admin", "admin", "branch_admin", "manager"]);
};

/**
 * Check if user is super admin
 * @param userRole - User's role
 * @returns boolean - True if user is super admin
 */
export const isSuperAdmin = (userRole: string): boolean => {
  return userRole === "super_admin";
};

/**
 * Check if user is admin or super admin
 * @param userRole - User's role
 * @returns boolean - True if user is admin or super admin
 */
export const isAdminOrSuperAdmin = (userRole: string): boolean => {
  return userRole === "super_admin" || userRole === "admin";
};

/**
 * Get all permissions for super admin
 * @returns Record<string, Record<string, boolean>> - All permissions set to true
 */
export const getSuperAdminPermissions = (): Record<string, Record<string, boolean>> => {
  const permissions: Record<string, Record<string, boolean>> = {};
  
  AVAILABLE_MODULES.forEach(module => {
    permissions[module] = {};
    AVAILABLE_ACTIONS.forEach(action => {
      permissions[module][action] = true;
    });
  });
  
  return permissions;
};

/**
 * Check if user has any permissions at all
 * @param userPermissions - User's permissions object
 * @param userRole - User's role
 * @param userRoles - User's RBAC roles array
 * @returns boolean - True if user has any permissions
 */
export const hasAnyPermission = (
  userPermissions: UserPermissions | null | undefined,
  userRole?: string,
  userRoles?: string[]
): boolean => {
  // Super admin has all permissions
  if (userRole === "super_admin" || (userRoles && userRoles.includes("super_admin"))) {
    return true;
  }

  // If no permissions object, user has no permissions
  if (!userPermissions || typeof userPermissions !== 'object') {
    return false;
  }

  // Check if user has any permission set to true across all modules
  for (const module in userPermissions) {
    const modulePermissions = userPermissions[module];
    if (modulePermissions && typeof modulePermissions === 'object') {
      for (const action in modulePermissions) {
        if (modulePermissions[action] === true) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Check if user has permission to access a specific report
 * @param userPermissions - User's permissions object
 * @param reportId - Report ID (e.g., 'work-order-by-item', 'order-status')
 * @param action - Action name (default: 'read')
 * @returns boolean - True if user has permission to access the report
 */
export const hasReportPermission = (
  userPermissions: UserPermissions | null | undefined,
  reportId: string,
  action: string = 'read'
): boolean => {
  // Strict check: if no permissions object, deny access
  if (!userPermissions || typeof userPermissions !== 'object') return false;
  
  // Check if reports module exists
  const reportsPermissions = userPermissions['reports'];
  if (!reportsPermissions || typeof reportsPermissions !== 'object') return false;
  
  // First, check for specific report permission
  // If individual permission is explicitly set (true or false), use it (takes precedence)
  const reportPermission = reportsPermissions[reportId];
  if (reportPermission && typeof reportPermission === 'object') {
    // Check if the action is explicitly set (not undefined)
    if (reportPermission[action] !== undefined) {
      return reportPermission[action] === true;
    }
  }
  
  // If no individual permission is set, fall back to reports.read (backward compatibility)
  // If reports.read is true, user has access to all reports
  if (reportsPermissions['read'] === true) {
    return true;
  }
  
  return false;
};
